import { useMemo, useState } from 'react';
import {
  IconChevronDown, IconChevronLeft, IconChevronRight, IconClock, IconMail, IconMapPin, IconPlus, IconRepeat, IconSquareCheck, IconVideo, IconX, IconBriefcase,
} from '@tabler/icons-react';
import { Page } from '@hana/shell';
import { useToast, type ScreenState } from '@hana/ui';
import {
  CALS, DOW, EVENTS, H0, H1, KIND_LABEL, KS, NOW_H, PX, RS_LABEL, RS_OPTS, TASKS, TODAY, VIEWER, WC, addDays, calOf, covers, dateOf, daysOf, fmtDM, hm, isOffDay, keyOf, layoutDay,
  ADD_OPTS, SL, blankNew, eventFromNew, hasConflict, isBusy, suggestSlots, type NewEv,
  mkDate, monOf, titleOf, whenOf, type CalEvent, type CalKey, type CalView, type Rsvp,
} from './data';
import s from './calendar.module.css';

// Mint-Box-Calendar (desktop 1536): lịch Box — tuần / tuần làm việc / ngày / tháng, lời mời, cut-off job, nghỉ phép HANA Mee.
// Dữ liệu mẫu, chưa nối Graph: tạo sự kiện + trợ lý chọn giờ chạy tại chỗ (state trong trang), "Gửi lời mời" chưa gửi thư thật (docs/streams/I.md).
const MINI_HEAD = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const VIEWS: ReadonlyArray<[CalView, string]> = [['day', 'Ngày'], ['work', 'Tuần làm việc'], ['week', 'Tuần'], ['month', 'Tháng']];

function readState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready';
}
function readView(): CalView {
  const v = new URLSearchParams(location.search).get('view');
  return v === 'day' || v === 'work' || v === 'month' ? v : 'week';
}

const timeOf = (e: CalEvent) => (e.all ? 'Cả ngày' : e.s === e.e ? hm(e.s!) : hm(e.s!) + '–' + hm(e.e!));

const Seg = <T extends string | number>({ label, opts, val, on }: { label: string; opts: ReadonlyArray<[T, string]>; val: T; on: (v: T) => void }) => (
  <div role="group" aria-label={label} className={s.nwSeg}>
    {opts.map(([k, l]) => <button key={String(k)} type="button" aria-pressed={val === k} className={`${s.nwSegB} ${val === k ? s.segOn : ''}`} onClick={() => on(k)}>{l}</button>)}
  </div>
);

function NewPanel({ nw, evs, upd, close, send }: { nw: NewEv; evs: CalEvent[]; upd: (p: Partial<NewEv>) => void; close: () => void; send: () => void }) {
  const sugs = suggestSlots(evs, nw);
  return (
    <>
      <div className={s.dHd}><h2 className={s.grow}>Sự kiện mới</h2><button type="button" className={s.dX} aria-label="Đóng" onClick={close}><IconX size={18} stroke={1.9} aria-hidden="true" /></button></div>
      <div className={s.nwBody}>
        <input className={s.nwIn} aria-label="Tiêu đề sự kiện" placeholder="Thêm tiêu đề" value={nw.k} onChange={(e) => upd({ k: e.target.value })} />
        <div className={s.nwWhen}><IconClock size={16} stroke={1.8} aria-hidden="true" /><b>{fmtDM(dateOf(nw.d))}/2026 · {hm(nw.s)} – {hm(nw.s + nw.dur)}</b></div>
        <Seg label="Thời lượng" opts={[[0.5, '30 phút'], [1, '1 giờ'], [1.5, '1,5 giờ']]} val={nw.dur} on={(dur) => upd({ dur })} />
        <div>
          <div className={s.cap0}>NGƯỜI THAM DỰ</div>
          <div className={s.nwChips}>
            {nw.att.map((a, i) => (
              <span key={a[0]} className={s.nwChip}>{a[0]}<button type="button" disabled={a[0] === VIEWER} aria-label={`Bỏ ${a[0]}`} onClick={() => upd({ att: nw.att.filter((_, j) => j !== i) })}>×</button></span>
            ))}
          </div>
          <div className={s.nwAdd}>
            {ADD_OPTS.filter((x) => !nw.att.some((a) => a[0] === x)).map((x) => <button key={x} type="button" className={s.nwAddB} onClick={() => upd({ att: nw.att.concat([[x, 'none']]) })}>+ {x}</button>)}
          </div>
        </div>
        <div className={s.nwAssist}>
          <div className={s.nwAsHd}><b>Trợ lý chọn giờ</b><span title="Ô đậm = bận · khung giờ theo WorkCalendar (tblClientInfo)">WorkCalendar</span></div>
          <div className={s.nwHead}>{SL.map((h, i) => <span key={h}>{i % 3 === 0 ? hm(h) : ''}</span>)}</div>
          {nw.att.map((a) => (
            <div key={a[0]} className={s.nwRow}>
              <span className={s.nwWho}>{a[0]}</span>
              {SL.map((h) => {
                const on = h >= nw.s && h < nw.s + nw.dur;
                const busy = isBusy(evs, a[0], nw.d, h);
                return <span key={h} className={`${s.nwCell} ${busy ? s.nwBusy : on ? s.nwOn : ''} ${on ? s.nwSel : ''}`} title={`${a[0]} · ${hm(h)}${busy ? ' · bận' : ' · rảnh'}`} />;
              })}
            </div>
          ))}
          <div className={s.nwSugs}>
            {sugs.map((h) => <button key={h} type="button" aria-pressed={nw.s === h} className={`${s.nwSug} ${nw.s === h ? s.nwSugOn : ''}`} onClick={() => upd({ s: h })}>{hm(h)} – {hm(h + nw.dur)}</button>)}
            {!sugs.length && <span className={s.nwNone}>Không có giờ cùng rảnh</span>}
          </div>
          {hasConflict(evs, nw) && <div role="alert" className={s.nwWarn}>Giờ đang chọn trùng lịch của người tham dự</div>}
        </div>
        <div className={s.nwLine}><span>Hình thức</span><Seg label="Hình thức" opts={[['teams', 'Teams'], ['office', 'Văn phòng'], ['cust', 'Tại khách']] as const} val={nw.place} on={(place) => upd({ place })} /></div>
        <div className={s.nwLine}><span>Nhắc trước</span><Seg label="Nhắc trước" opts={[[0, 'Không'], [15, '15 phút'], [60, '1 giờ']]} val={nw.rem} on={(rem) => upd({ rem })} /></div>
        <div className={s.nwLine}><span>Lặp lại</span><Seg label="Lặp lại" opts={[['none', 'Không lặp'], ['wd', 'Ngày làm việc'], ['wk', 'Hằng tuần']] as const} val={nw.rep} on={(rep) => upd({ rep })} /></div>
        <label className={s.nwLine}><span>Gắn job</span><input className={s.nwIn2} aria-label="Gắn job" placeholder="vd SE260639142" value={nw.job} onChange={(e) => upd({ job: e.target.value })} /></label>
      </div>
      <div className={s.nwFoot}><button type="button" className={s.nwCancel} onClick={close}>Huỷ</button><button type="button" className={s.nwSend} onClick={send}>Gửi lời mời</button></div>
    </>
  );
}

export function CalendarPage() {
  const toast = useToast();
  const state = readState();
  const [view, setView] = useState<CalView>(readView);
  const [anchor, setAnchor] = useState(() => dateOf(TODAY));
  const [sel, setSel] = useState<number | null>(null);
  const [hid, setHid] = useState<Partial<Record<CalKey, boolean>>>({});
  const [rsvp, setRsvp] = useState<Record<number, Rsvp>>({});
  const [extra, setExtra] = useState<CalEvent[]>([]);
  const [nw, setNw] = useState<NewEv | null>(null);
  const openNew = (d: string, h: number, dur = 1) => { setNw(blankNew(d, h, dur)); setSel(null); };

  const all = useMemo(() => EVENTS.concat(extra).map((e) => ({ ...e, rs: rsvp[e.id] ?? (e.kind === 'inv' ? ('none' as Rsvp) : ('yes' as Rsvp)) })), [rsvp, extra]);
  type Ev = (typeof all)[number];
  const vis: Ev[] = state === 'empty' ? [] : all.filter((e) => !hid[calOf(e)]);
  const cur = sel != null ? all.find((e) => e.id === sel) ?? null : null;
  const invs = all.filter((e) => e.kind === 'inv' && e.rs === 'none');

  const days = daysOf(view, anchor);
  const step = view === 'day' ? 1 : view === 'month' ? 28 : 7;
  const go = (n: number) => {
    setAnchor(view === 'month' ? mkDate(anchor.getUTCFullYear(), anchor.getUTCMonth() + n, 1) : addDays(anchor, n * step));
    setSel(null);
  };
  const reply = (e: Ev, r: Exclude<Rsvp, 'none'>) => {
    setRsvp((p) => ({ ...p, [e.id]: r }));
    toast({ msg: `${r === 'yes' ? 'Đã chấp nhận' : r === 'maybe' ? 'Đã trả lời Có thể' : 'Đã từ chối'} “${e.k}” · đã gửi thư trả lời cho ${e.org ?? 'người mời'}` });
  };

  const upd = (p: Partial<NewEv>) => setNw((n) => (n ? { ...n, ...p } : n));
  const send = () => {
    if (!nw) return;
    const ev = eventFromNew(100 + extra.length, nw);
    setExtra((x) => x.concat(ev));
    setNw(null);
    setSel(ev.id);
    toast({ msg: `Đã tạo “${ev.k}” · ${fmtDM(dateOf(nw.d))} ${hm(nw.s)} · ${nw.att.length} người · chưa gửi thư thật ở bản này` });
  };
  const m0 = mkDate(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1);
  const g0 = monOf(m0);
  const todayD = dateOf(TODAY);
  const weekKeys = view === 'month' ? [] : days.map(keyOf);

  const evBtn = (e: Ev, cls: string | undefined, children: React.ReactNode, title?: string, pos?: React.CSSProperties) => {
    const [bg, bar, tx] = KS[e.kind];
    const pend = e.kind === 'inv' && e.rs === 'none';
    const on = sel === e.id;
    return (
      <button
        type="button"
        className={`${s.ev} ${cls ?? ''} ${pend ? s.evPend : ''} ${e.rs === 'no' ? s.evNo : ''} ${on ? s.evOn : ''}`}
        key={e.id}
        style={{ ...pos, '--ev-bg': bg, '--ev-bar': bar, '--ev-tx': e.rs === 'no' ? 'var(--lb)' : tx } as React.CSSProperties}
        aria-label={`${e.k} · ${e.all ? 'cả ngày' : hm(e.s!)}${pend ? ' · chưa trả lời' : ''}`}
        aria-pressed={on}
        title={title}
        onClick={() => setSel(e.id)}
      >
        {children}
      </button>
    );
  };

  const att = (e: Ev): Array<{ k: string; st: string; c: string; ab: string }> =>
    e.att.map(([k, st]) => {
      const r = RS_LABEL[k === VIEWER ? e.rs : st];
      return { k, st: r[0], c: r[1], ab: k.replace(/[^A-Za-zÀ-ỹ]/g, '').slice(0, 2).toUpperCase() };
    });
  const isRsvpable = (e: Ev) => e.kind === 'inv' || (!!e.org && e.org !== VIEWER && e.att.some((a) => a[0] === VIEWER));

  const todayList = vis.filter((e) => e.d === TODAY && !e.all).sort((a, b) => a.s! - b.s!);
  const grid = state !== 'loading' && state !== 'error' && view !== 'month';
  const anyAll = days.some((dt) => vis.some((e) => e.all && covers(e, dt)));

  return (
    <Page kind="tool">
      <div className={s.page}>
        <main className={s.main}>
          <header className={s.head}>
            <h1 className={s.sr}>Lịch HANA Box</h1>
            <button type="button" className={s.today} title="Về hôm nay · T" onClick={() => { setAnchor(todayD); setSel(null); }}>Hôm nay</button>
            <button type="button" className={s.nav} aria-label="Trước (Alt ←)" title="Trước · Alt ←" onClick={() => go(-1)}><IconChevronLeft size={18} stroke={1.9} aria-hidden="true" /></button>
            <button type="button" className={s.nav} aria-label="Sau (Alt →)" title="Sau · Alt →" onClick={() => go(1)}><IconChevronRight size={18} stroke={1.9} aria-hidden="true" /></button>
            <b className={s.title}>{titleOf(view, anchor)}</b>
            <span className={s.grow} />
            <div role="tablist" aria-label="Chế độ xem" className={s.seg}>
              {VIEWS.map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={view === k} className={`${s.segB} ${view === k ? s.segOn : ''}`} onClick={() => { setView(k); setSel(null); }}>{l}</button>
              ))}
            </div>
            <a className={s.outlook} href="https://outlook.office.com/calendar" target="_blank" rel="noopener" title="Mở lịch trong Outlook web">Outlook web ↗</a>
            <div className={s.split}>
              <button type="button" className={s.newEv} title="Sự kiện mới · N" onClick={() => openNew(TODAY, 10.5)}><IconPlus size={16} stroke={2} aria-hidden="true" />Sự kiện mới</button>
              <button type="button" className={s.newMore} aria-label="Chọn loại sự kiện" aria-haspopup="menu" onClick={() => openNew(TODAY, 14.5, 0.5)}><IconChevronDown size={15} stroke={2} aria-hidden="true" /></button>
            </div>
          </header>

          <div className={s.cols}>
            <section className={s.left} aria-label="Lịch nhỏ và các lịch">
              <div>
                <div className={s.miniHd}>
                  <b>{`Tháng ${anchor.getUTCMonth() + 1} · ${anchor.getUTCFullYear()}`}</b>
                  <span className={s.miniNav}>
                    <button type="button" aria-label="Tháng trước" onClick={() => setAnchor(mkDate(anchor.getUTCFullYear(), anchor.getUTCMonth() - 1, 1))}><IconChevronLeft size={18} stroke={1.9} aria-hidden="true" /></button>
                    <button type="button" aria-label="Tháng sau" onClick={() => setAnchor(mkDate(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 1))}><IconChevronRight size={18} stroke={1.9} aria-hidden="true" /></button>
                  </span>
                </div>
                <div role="grid" aria-label="Lịch tháng" className={s.mini}>
                  {MINI_HEAD.map((h) => <span key={h} className={s.miniDow}>{h}</span>)}
                  {Array.from({ length: 42 }, (_, i) => {
                    const dt = addDays(g0, i);
                    const k = keyOf(dt);
                    const inWk = weekKeys.includes(k);
                    const inM = dt.getUTCMonth() === anchor.getUTCMonth();
                    const has = vis.some((e) => e.d === k);
                    const isT = k === TODAY;
                    return (
                      <button key={k} type="button" className={`${s.miniD} ${isT ? s.miniT : ''} ${inWk && !isT ? s.miniW : ''} ${!inM && !isT ? s.miniOut : ''}`} aria-label={`${fmtDM(dt)}/${dt.getUTCFullYear()}`} onClick={() => { setAnchor(dt); setSel(null); }}>
                        {dt.getUTCDate()}
                        {has && !isT && <span className={s.miniDot} />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className={s.cap}>LỊCH</div>
                {CALS.map((c) => (
                  <label key={c.key} className={s.calRow}>
                    <input type="checkbox" checked={!hid[c.key]} onChange={() => setHid((h) => ({ ...h, [c.key]: !h[c.key] }))} style={{ accentColor: c.color }} />
                    <span className={s.grow}>{c.label}</span>
                    <span className={s.calDot} style={{ background: c.color }} />
                  </label>
                ))}
              </div>
              <div>
                <div className={s.invHd}><span className={s.cap0}>LỜI MỜI CHỜ TRẢ LỜI</span><span className={s.invN}>{invs.length}</span></div>
                {invs.map((e) => (
                  <div key={e.id} className={s.inv}>
                    <button type="button" className={s.invOpen} onClick={() => { setSel(e.id); setAnchor(dateOf(e.d)); }}>
                      <b>{e.k}</b>
                      <span>{fmtDM(dateOf(e.d))} · {hm(e.s!)} · {e.org}</span>
                    </button>
                    <div role="radiogroup" aria-label="Trả lời lời mời" className={s.rs}>
                      {RS_OPTS.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={e.rs === k} className={`${s.rsB} ${e.rs === k ? s.segOn : ''}`} onClick={() => reply(e, k)}>{l}</button>)}
                    </div>
                  </div>
                ))}
                {invs.length === 0 && <div className={s.invNone}>Đã trả lời hết lời mời.</div>}
              </div>
              <div className={s.wc} title="Giờ làm việc và ngày nghỉ lấy từ WorkCalendar (tblClientInfo) — sửa ở HR Dashboard">
                {`Ca ${hm(WC.am[0])}–${hm(WC.am[1])} · ${hm(WC.pm[0])}–${hm(WC.pm[1])} · nguồn ${WC.src}`}
              </div>
            </section>

            <section className={s.gridBox} aria-label="Lưới lịch">
              {state === 'loading' && (
                <div aria-busy="true" className={s.sk}><div className={s.skA} /><div className={s.skB} /></div>
              )}
              {state === 'error' && (
                <div className={s.stErr}>
                  <b>Không tải được lịch</b>
                  <span>Mất kết nối tới máy chủ lịch lúc 09:02 · đang hiện bản lưu 08:58.</span>
                  <button type="button" className={s.today} onClick={() => toast({ msg: 'Đang tải lại lịch…' })}>Thử lại</button>
                </div>
              )}
              {grid && (
                <>
                  {state === 'noperm' && <div role="status" className={s.warnNote}>Chưa có quyền xem <b>Lịch nhóm CS / BKG</b> · trưởng nhóm chia sẻ ở Outlook › Chia sẻ lịch.</div>}
                  {state === 'empty' && <div role="status" className={s.emptyNote}>Khoảng này chưa có lịch · bấm vào ô giờ để tạo sự kiện.</div>}
                  <div className={s.dayHd}>
                    {days.map((dt) => {
                      const isT = keyOf(dt) === TODAY;
                      return (
                        <button key={keyOf(dt)} type="button" className={`${s.dh} ${isT ? s.dhT : ''}`} title={`Xem ngày ${fmtDM(dt)}`} onClick={() => { setView('day'); setAnchor(dt); setSel(null); }}>
                          <b>{DOW[dt.getUTCDay()]} {dt.getUTCDate()}</b><span>{fmtDM(dt)}</span>
                        </button>
                      );
                    })}
                  </div>
                  {anyAll && (
                    <div className={s.allRow}>
                      {days.map((dt) => (
                        <div key={keyOf(dt)} className={s.allCol}>
                          {vis.filter((e) => e.all && covers(e, dt)).map((e) => evBtn(e, s.evAll, e.k))}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={s.scroll}>
                    <div className={s.tg} style={{ height: (H1 - H0) * PX }}>
                      <div className={s.hours}>
                        {Array.from({ length: H1 - H0 }, (_, i) => H0 + i).map((h) => <span key={h} style={{ top: (h - H0) * PX }}>{hm(h)}</span>)}
                      </div>
                      {days.map((dt) => {
                        const k = keyOf(dt);
                        const off = dt.getUTCDay() === 0 || dt.getUTCDay() === 6 || isOffDay(vis, dt);
                        const lay = layoutDay(vis.filter((e) => e.d === k));
                        return (
                          <div key={k} className={`${s.col} ${off ? s.colOff : ''}`}>
                            {Array.from({ length: H1 - H0 }, (_, i) => H0 + i).map((h) => {
                              const dim = h < WC.am[0] || h >= WC.pm[1] || (h >= WC.am[1] && h + 1 <= WC.pm[0]);
                              const tip = `${fmtDM(dt)} ${hm(h)} · bấm để tạo sự kiện`;
                              return <button key={h} type="button" className={`${s.cell} ${dim ? s.cellDim : ''}`} aria-label={tip} title={tip} onClick={() => openNew(k, h)} />;
                            })}
                            {lay.map(({ e, ln, n }) => {
                              const ev = all.find((x) => x.id === e.id)!;
                              const h = e.s === e.e ? 20 : Math.max(22, (e.e! - e.s!) * PX - 3);
                              const w = 100 / n;
                              return (
                                evBtn(ev, h < 40 ? s.evShort : s.evTall,
                                  h < 40 ? <span className={s.evLine}><b>{timeOf(e)}</b> {e.k}</span> : <><b className={s.evK}>{e.k}</b><span className={s.evSub}>{timeOf(e)} · {e.place ?? ''}</span></>,
                                  `${e.k} · ${timeOf(e)} · ${e.place ?? ''}`,
                                  { top: (e.s! - H0) * PX, height: h, left: `calc(${ln * w}% + 3px)`, width: `calc(${w}% - 6px)` })
                              );
                            })}
                            {k === TODAY && <div className={s.now} style={{ top: (NOW_H - H0) * PX }} aria-label="Bây giờ 09:14"><span /></div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              {state !== 'loading' && state !== 'error' && view === 'month' && (
                <>
                  {state === 'noperm' && <div role="status" className={s.warnNote}>Chưa có quyền xem <b>Lịch nhóm CS / BKG</b> · trưởng nhóm chia sẻ ở Outlook › Chia sẻ lịch.</div>}
                  {state === 'empty' && <div role="status" className={s.emptyNote}>Khoảng này chưa có lịch · bấm vào ô giờ để tạo sự kiện.</div>}
                  <div className={s.mHead}>{MINI_HEAD.map((h) => <span key={h}>{h}</span>)}</div>
                  <div className={s.mBody}>
                    {Array.from({ length: 6 }, (_, w) => (
                      <div key={w} className={s.mRow}>
                        {Array.from({ length: 7 }, (_, i) => {
                          const dt = addDays(g0, w * 7 + i);
                          const evs = vis.filter((e) => covers(e, dt)).sort((a, b) => (a.all ? -1 : a.s!) - (b.all ? -1 : b.s!));
                          const inM = dt.getUTCMonth() === anchor.getUTCMonth();
                          const isT = keyOf(dt) === TODAY;
                          const offD = dt.getUTCDay() % 6 === 0 || isOffDay(vis, dt);
                          return (
                            <div key={i} className={`${s.mDay} ${!inM || offD ? s.mDayOff : ''}`}>
                              <button type="button" className={`${s.mN} ${isT ? s.mNT : ''} ${!inM && !isT ? s.mNOut : ''}`} aria-label={`Xem ngày ${dt.getUTCDate()}`} onClick={() => { setView('day'); setAnchor(dt); setSel(null); }}>{dt.getUTCDate()}</button>
                              {evs.slice(0, 3).map((e) => evBtn(e, s.evMonth, e.k, `${timeOf(e)} · ${e.k}`))}
                              {evs.length > 3 && <button type="button" className={s.more} onClick={() => { setView('day'); setAnchor(dt); setSel(null); }}>{`+${evs.length - 3} sự kiện`}</button>}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

            <section className={s.right} aria-label="Chi tiết">
              {nw ? <NewPanel nw={nw} evs={all} upd={upd} close={() => setNw(null)} send={send} /> : !cur ? (
                <>
                  <div className={s.rHd}><div className={s.cap0}>HÔM NAY CỦA TÔI</div><b>{DOW[todayD.getUTCDay()]} {fmtDM(todayD)}/2026</b></div>
                  <div className={s.rBody}>
                    {todayList.map((e) => (
                      evBtn(e, s.evCard, <><b>{e.k}</b><span>{timeOf(e)} · {e.place ?? ''}</span></>)
                    ))}
                    <div className={`${s.cap0} ${s.capTop}`}>VIỆC CÓ HẠN · VIỆC &amp; DUYỆT</div>
                    {TASKS.map(([k, v]) => (
                      <div key={k} className={s.task}>
                        <span className={s.taskIc}><IconSquareCheck size={16} stroke={1.9} aria-hidden="true" /></span>
                        <span className={s.taskTx}><span>{k}</span><span>{v}</span></span>
                      </div>
                    ))}
                    <a className={s.tasksLink} href="/ezy/tasks">Mở Việc &amp; Duyệt ↗</a>
                  </div>
                </>
              ) : (
                <>
                  <div className={s.dHd}>
                    <div className={s.grow}><div className={s.dKind}>{KIND_LABEL[cur.kind]}</div><h2>{cur.k}</h2></div>
                    <button type="button" className={s.dX} aria-label="Đóng chi tiết" onClick={() => setSel(null)}><IconX size={18} stroke={1.9} aria-hidden="true" /></button>
                  </div>
                  <div className={s.dBody}>
                    <div className={s.dRow}><IconClock size={16} stroke={1.9} aria-hidden="true" /><span>{whenOf(cur)}</span></div>
                    {cur.rep && <div className={s.dRow}><IconRepeat size={16} stroke={1.9} aria-hidden="true" /><span>{cur.rep}</span></div>}
                    {cur.place && <div className={s.dRow}><IconMapPin size={16} stroke={1.9} aria-hidden="true" /><span>{cur.place}</span></div>}
                    {cur.job && <a className={s.jobChip} href="/ezy/jobs"><IconBriefcase size={14} stroke={1.9} aria-hidden="true" />{cur.job} ↗</a>}
                    {cur.mail && <a className={s.mailLink} href="/box"><IconMail size={16} stroke={1.9} aria-hidden="true" /><span>Thư gốc · {cur.mail}</span></a>}
                    {/Teams/.test(cur.place ?? '') && <button type="button" className={s.join} onClick={() => toast({ msg: 'Đang mở Microsoft Teams…' })}><IconVideo size={16} stroke={1.9} aria-hidden="true" />Tham gia Teams</button>}
                    {isRsvpable(cur) && (
                      <div>
                        <div className={`${s.cap0} ${s.capGap}`}>TRẢ LỜI</div>
                        <div role="radiogroup" aria-label="Trả lời lời mời" className={s.rs}>
                          {RS_OPTS.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={cur.rs === k} className={`${s.rsB} ${s.rsBig} ${cur.rs === k ? s.segOn : ''}`} onClick={() => reply(cur, k)}>{l}</button>)}
                        </div>
                      </div>
                    )}
                    {cur.kind === 'cut' && <div className={s.cutNote}>Mốc của hãng tàu · đổi ở job HANA Ezy, lịch tự cập nhật. Nhắc trước theo giờ làm việc (WorkCalendar).</div>}
                    {cur.att.length > 0 && (
                      <div>
                        <div className={`${s.cap0} ${s.capGap}`}>NGƯỜI THAM DỰ · {cur.att.length} người</div>
                        {att(cur).map((a) => (
                          <div key={a.k} className={s.att}>
                            <span className={s.attAv} aria-hidden="true">{a.ab}</span>
                            <span className={s.attK}>{a.k}</span>
                            <span className={s.attSt} style={{ color: a.c }}>{a.st}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>
        </main>
      </div>
    </Page>
  );
}
